import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import { isNativeRuntime } from '../../utils/runtimePlatform';

const STORAGE_KEY = 'senerfi_user_prefs_v2';
const AUTH_TOKEN_KEY = 'senerfi_auth_token';
const SIG_MOBILE_OFFLINE_SESSION_KEY = 'senerfi_sig_mobile_offline_session_v1';
const SIG_MOBILE_VARIANT = 'sig_mobile';

// El backend (login.php / verify_2fa.php / totp_enroll.php) devuelve estos
// permisos en snake_case (como están en la tabla admin_users), pero el resto
// de la app los lee en camelCase (currentUser.overtimeCanEditApproved, etc).
// Sin esta normalización, un permiso recién habilitado desde Administración
// TI no se reflejaba al iniciar sesión: solo aparecía tras una recarga
// completa del navegador que disparara el fetch aparte de admin.users.list.
function normalizePermissionFlags(u) {
  if (!u || typeof u !== 'object') return u;
  return {
    ...u,
    purchaseCanApproveRequests: !!Number(u.purchase_can_approve_requests ?? u.purchaseCanApproveRequests ?? 0),
    purchaseCanApproveOrders: !!Number(u.purchase_can_approve_orders ?? u.purchaseCanApproveOrders ?? 0),
    purchaseCanManageRequests: !!Number(u.purchase_can_manage_requests ?? u.purchaseCanManageRequests ?? 0),
    purchaseCanEditOrders: !!Number(u.purchase_can_edit_orders ?? u.purchaseCanEditOrders ?? 0),
    purchaseCanManagePaymentStatus: !!Number(u.purchase_can_manage_payment_status ?? u.purchaseCanManagePaymentStatus ?? 0),
    overtimeFinalApprover: !!Number(u.overtime_final_approver ?? u.overtimeFinalApprover ?? 0),
    preopAuthSst: !!Number(u.preop_auth_sst ?? u.preopAuthSst ?? 0),
    preopAuthSeguridad: !!Number(u.preop_auth_seguridad ?? u.preopAuthSeguridad ?? 0),
    preopAuthTransportes: !!Number(u.preop_auth_transportes ?? u.preopAuthTransportes ?? 0),
    overtimeCanEditApproved: !!Number(u.overtime_can_edit_approved ?? u.overtimeCanEditApproved ?? 0),
    hrNoveltiesCanExport: !!Number(u.hr_novelties_can_export ?? u.hrNoveltiesCanExport ?? 0),
    expenseCanExport: !!Number(u.expense_can_export ?? u.expenseCanExport ?? 0),
    expenseCanManage: !!Number(u.expense_can_manage ?? u.expenseCanManage ?? 0),
    noveltyHrCanReceive: !!Number(u.novelty_hr_can_receive ?? u.noveltyHrCanReceive ?? 0),
    preopCanApprove: !!Number(u.preop_can_approve ?? u.preopCanApprove ?? 0),
    overtimeAllowedProjects: u.overtime_allowed_projects ?? u.overtimeAllowedProjects ?? '',
  };
}

const defaultPrefs = {
  users: [
    { id: 1, name: 'Admin TI', email: 'admin@senerfi.com', role: 'Administrador', password: '1234', warehouseId: 'popayan' },
  ],
  currentUserId: null,
  modulePrefs: {
    // por usuario: visibilidad de módulos
    1: { 'recursos-humanos': true, 'almacen': true, 'sgi': true, 'contabilidad': true, 'transportes': true, 'seguridad': true, 'administracion-ti': true, 'control-operativo': true },
    '-1': { 'recursos-humanos': true, 'almacen': true, 'sgi': true, 'contabilidad': true, 'transportes': true, 'seguridad': true, 'administracion-ti': true, 'control-operativo': true },
  },
  sectionPrefs: {
    // por usuario: permisos por sección dentro de cada módulo
    1: {
      'almacen': {
        'marca-item': true,
        'materiales': true,
        'bodegas': true,
        'pedidos': true,
        'entrada': true,
        'asignacion': true,
        'prestamo': true,
        'devoluciones': true,
        'salidas': true,
        'traslados': true,
        'consumibles': true,
        'stock': true,
        'consulta-responsable': true,
        'consulta-serial': true,
        'historial': true,
        'alertas': true,
        'indicadores': true,
      },
      'recursos-humanos': {
        'personas': true,
      },
      'contabilidad': {
        'libro-diario': true,
        'cuentas': true,
        'facturacion': true,
        'reportes': true,
        'solicitudes': true,
        'proveedores': true,
        'centros-costo': true,
        'novedades': true,
        'solicitud-gastos': true,
      },
      'transportes': {
        'flota': true,
        'preoperacionales': true,
        'mantenimientos': true,
        'asignacion_vehiculos': true,
      },
      'seguridad': {
        'inspecciones': true,
        'incidentes': true,
        'reportes': true,
      },
      'control-operativo': {
        'planificacion': true,
        'seguimiento': true,
        'incidencias': true,
        'reportes': true,
      },
    },
    '-1': {
      'almacen': {
        'marca-item': true,
        'materiales': true,
        'bodegas': true,
        'pedidos': true,
        'entrada': true,
        'asignacion': true,
        'prestamo': true,
        'devoluciones': true,
        'salidas': true,
        'traslados': true,
        'consumibles': true,
        'stock': true,
        'consulta-responsable': true,
        'consulta-serial': true,
        'historial': true,
        'alertas': true,
        'indicadores': true,
      },
      'recursos-humanos': {
        'personas': true,
      },
      'contabilidad': {
        'libro-diario': true,
        'cuentas': true,
        'facturacion': true,
        'reportes': true,
        'solicitudes': true,
        'proveedores': true,
        'centros-costo': true,
        'novedades': true,
        'solicitud-gastos': true,
      },
      'transportes': {
        'flota': true,
        'preoperacionales': true,
        'mantenimientos': true,
        'asignacion_vehiculos': true,
      },
      'seguridad': {
        'inspecciones': true,
        'incidentes': true,
        'reportes': true,
      },
      'control-operativo': {
        'planificacion': true,
        'seguimiento': true,
        'incidencias': true,
        'reportes': true,
      },
    },
  },
};

const UserPrefsContext = createContext(null);

function isSigMobileVariant() {
  return String(process.env.REACT_APP_APP_VARIANT || '').trim().toLowerCase() === SIG_MOBILE_VARIANT;
}

function canUseSigMobileOfflineAuth() {
  return isNativeRuntime() && isSigMobileVariant();
}

function readJsonStorage(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage?.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (_) {
    return fallback;
  }
}

function writeJsonStorage(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.setItem(key, JSON.stringify(value));
  } catch (_) {}
}

function clearSigMobileOfflineSession() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.removeItem(SIG_MOBILE_OFFLINE_SESSION_KEY);
  } catch (_) {}
}

function normalizeLoginIdentifier(value) {
  return String(value || '').trim().toLowerCase();
}

async function computeOfflineLoginDigest(identifier, password) {
  const normalized = `${normalizeLoginIdentifier(identifier)}::${String(password || '')}`;
  try {
    if (typeof window !== 'undefined' && window.crypto?.subtle && typeof TextEncoder !== 'undefined') {
      const data = new TextEncoder().encode(normalized);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
      return Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (_) {}
  let hash = 0;
  for (let i = 0; i < normalized.length; i += 1) {
    hash = ((hash << 5) - hash + normalized.charCodeAt(i)) | 0;
  }
  return `fallback-${hash}`;
}

async function persistSigMobileOfflineSession({ user, identifier, password }) {
  if (!canUseSigMobileOfflineAuth() || !user || !password) return;
  const identifiers = Array.from(new Set([
    normalizeLoginIdentifier(identifier),
    normalizeLoginIdentifier(user?.email),
    normalizeLoginIdentifier(user?.responsibleCedula),
    normalizeLoginIdentifier(user?.responsible_cedula),
    normalizeLoginIdentifier(user?.cedula),
    normalizeLoginIdentifier(user?.id),
  ].filter(Boolean)));
  if (!identifiers.length) return;
  const authDigests = [];
  for (const value of identifiers) {
    authDigests.push({
      identifier: value,
      digest: await computeOfflineLoginDigest(value, password),
    });
  }
  writeJsonStorage(SIG_MOBILE_OFFLINE_SESSION_KEY, {
    savedAt: new Date().toISOString(),
    user,
    authDigests,
  });
}

async function resolveSigMobileOfflineSession(identifier, password) {
  if (!canUseSigMobileOfflineAuth()) return null;
  const offlineSession = readJsonStorage(SIG_MOBILE_OFFLINE_SESSION_KEY, null);
  if (!offlineSession?.user || !Array.isArray(offlineSession?.authDigests) || !offlineSession.authDigests.length) {
    return null;
  }
  const digest = await computeOfflineLoginDigest(identifier, password);
  const match = offlineSession.authDigests.some((entry) => String(entry?.digest || '') === digest);
  if (!match) return null;
  return offlineSession.user;
}

export function UserPrefsProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : defaultPrefs;
    } catch (e) {
      return defaultPrefs;
    }
  });

  const isOnline = (typeof navigator !== 'undefined') ? navigator.onLine : true;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {}
  }, [state]);

  // Asegurar usuarios predeterminados (BR-1..BR-5 y Brigada) aunque existan prefs previas en localStorage
  useEffect(() => {
    try {
      setState((s) => {
        const existing = Array.isArray(s.users) ? s.users : [];
        const baseMap = new Map();
        existing.forEach((u) => baseMap.set(String(u.email).toLowerCase(), u));
        const toEnsure = Array.isArray(defaultPrefs.users) ? defaultPrefs.users : [];
        toEnsure.forEach((u) => {
          const key = String(u.email).toLowerCase();
          if (!baseMap.has(key)) baseMap.set(key, u);
        });
        const mergedUsers = Array.from(baseMap.values());
        const nextModule = { ...s.modulePrefs };
        const nextSection = { ...s.sectionPrefs };
        toEnsure.forEach((u) => {
          if (!nextModule[u.id] && defaultPrefs.modulePrefs[u.id]) nextModule[u.id] = { ...defaultPrefs.modulePrefs[u.id] };
          if (!nextSection[u.id] && defaultPrefs.sectionPrefs[u.id]) nextSection[u.id] = { ...defaultPrefs.sectionPrefs[u.id] };
        });
        return { ...s, users: mergedUsers, modulePrefs: nextModule, sectionPrefs: nextSection };
      });
    } catch (_) {}
  }, [isOnline]);

  useEffect(() => {
    try {
      setState((s) => {
        const filteredUsers = (Array.isArray(s.users) ? s.users : []).filter((u) => !/^br\d+@senerfi\.com$/i.test(String(u.email || '')));
        const removedIds = new Set((Array.isArray(s.users) ? s.users : []).filter((u) => !filteredUsers.includes(u)).map((u) => u.id));
        const nextModule = { ...(s.modulePrefs || {}) };
        const nextSection = { ...(s.sectionPrefs || {}) };
        Array.from(removedIds).forEach((id) => { delete nextModule[id]; delete nextSection[id]; });
        return { ...s, users: filteredUsers, modulePrefs: nextModule, sectionPrefs: nextSection };
      });
    } catch (_) {}
  }, [isOnline]);

  // Cargar usuarios desde backend al iniciar
  useEffect(() => {
    if (!isOnline) return;
    if (!hasAuthToken()) return;
    (async () => {
      try {
        const res = await api.admin.users.list({ limit: 500, order: 'desc' });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const normalized = rows.map((u) => ({
          id: Number(u.id) || Date.now(),
          name: u.name,
          email: u.email,
          role: u.role || 'Consulta',
          password: u.password || '1234',
          warehouseId: u.warehouse_id || u.warehouseId || 'popayan',
          responsibleCedula: u.responsible_cedula || u.responsableCedula || null,
          areaName: u.area_name || u.areaName || '',
          active: u.active === undefined ? true : !!Number(u.active),
          purchaseCanApproveRequests: !!Number(u.purchase_can_approve_requests ?? u.purchaseCanApproveRequests ?? 0),
          purchaseCanApproveOrders: !!Number(u.purchase_can_approve_orders ?? u.purchaseCanApproveOrders ?? 0),
          purchaseCanManageRequests: !!Number(u.purchase_can_manage_requests ?? u.purchaseCanManageRequests ?? 0),
          purchaseCanEditOrders: !!Number(u.purchase_can_edit_orders ?? u.purchaseCanEditOrders ?? 0),
          purchaseCanManagePaymentStatus: !!Number(u.purchase_can_manage_payment_status ?? u.purchaseCanManagePaymentStatus ?? 0),
          overtimeFinalApprover: !!Number(u.overtime_final_approver ?? u.overtimeFinalApprover ?? 0),
          preopAuthSst: !!Number(u.preop_auth_sst ?? u.preopAuthSst ?? 0),
          preopAuthSeguridad: !!Number(u.preop_auth_seguridad ?? u.preopAuthSeguridad ?? 0),
          preopAuthTransportes: !!Number(u.preop_auth_transportes ?? u.preopAuthTransportes ?? 0),
          overtimeCanEditApproved: !!Number(u.overtime_can_edit_approved ?? u.overtimeCanEditApproved ?? 0),
          hrNoveltiesCanExport: !!Number(u.hr_novelties_can_export ?? u.hrNoveltiesCanExport ?? 0),
          expenseCanExport: !!Number(u.expense_can_export ?? u.expenseCanExport ?? 0),
          expenseCanManage: !!Number(u.expense_can_manage ?? u.expenseCanManage ?? 0),
    noveltyHrCanReceive: !!Number(u.novelty_hr_can_receive ?? u.noveltyHrCanReceive ?? 0),
    preopCanApprove: !!Number(u.preop_can_approve ?? u.preopCanApprove ?? 0),
          overtimeAllowedProjects: u.overtime_allowed_projects ?? u.overtimeAllowedProjects ?? '',
        }));
        if (normalized.length) {
          setState((s) => {
            const existing = Array.isArray(s.users) ? s.users : [];
            const map = new Map();
            existing.forEach((u) => { map.set(String(u.email).toLowerCase(), u); });
            normalized.forEach((u) => { map.set(String(u.email).toLowerCase(), u); });
            const merged = Array.from(map.values());
            return {
              ...s,
              users: merged,
              currentUserId: s.currentUserId || null,
            };
          });
        }
      } catch (_) {}
    })();
  }, [isOnline]);

  // Cargar preferencias de módulo/sección desde backend cuando cambia el usuario actual
  useEffect(() => {
    if (!state.currentUserId) return;
    if (!isOnline) return;
    (async () => {
      try {
        const res = await api.admin.prefs.get({ user_id: state.currentUserId });
        const mpRaw = res?.data?.module_prefs ?? res?.module_prefs;
        const spRaw = res?.data?.section_prefs ?? res?.section_prefs;
        const parseMaybe = (val) => {
          if (!val) return {};
          if (typeof val === 'string') {
            try { return JSON.parse(val); } catch { return {}; }
          }
          return val;
        };
        setState((s) => ({
          ...s,
          modulePrefs: { ...s.modulePrefs, [s.currentUserId]: parseMaybe(mpRaw) },
          sectionPrefs: { ...s.sectionPrefs, [s.currentUserId]: parseMaybe(spRaw) },
        }));
      } catch (_) {}
    })();
  }, [isOnline, state.currentUserId]);

  // Cargar permisos por defecto (usuario virtual -1) al iniciar
  useEffect(() => {
    if (!isOnline) return;
    if (!hasAuthToken()) return;
    (async () => {
      try {
        const res = await api.admin.prefs.get({ user_id: -1 });
        const mpRaw = res?.data?.module_prefs ?? res?.module_prefs;
        const spRaw = res?.data?.section_prefs ?? res?.section_prefs;
        const parseMaybe = (val) => {
          if (!val) return {};
          if (typeof val === 'string') {
            try { return JSON.parse(val); } catch { return {}; }
          }
          return val;
        };
        setState((s) => ({
          ...s,
          modulePrefs: { ...s.modulePrefs, [-1]: parseMaybe(mpRaw) },
          sectionPrefs: { ...s.sectionPrefs, [-1]: parseMaybe(spRaw) },
        }));
      } catch (_) {}
    })();
  }, [isOnline]);

  const users = state.users;
  const currentUser = useMemo(() => (state.currentUserId ? users.find(u => u.id === state.currentUserId) : null), [users, state.currentUserId]);

  const addUser = async (user) => {
    const idFallback = Date.now();
    const newUser = { id: idFallback, password: '1234', ...user };
    if (!newUser.warehouseId) newUser.warehouseId = 'popayan';
    const role = newUser.role || 'Administrativo';
    const roleModuleDefaults = (r) => {
      switch (r) {
        case 'Administrador':
          return { 'recursos-humanos': true, 'almacen': true, 'sgi': true, 'contabilidad': true, 'transportes': true, 'seguridad': true, 'administracion-ti': true, 'control-operativo': true };
        case 'Administrativo':
          return { 'sgi': true };
        case 'Operativo':
          return { 'recursos-humanos': false, 'almacen': false, 'sgi': true, 'contabilidad': false, 'transportes': false, 'seguridad': false, 'administracion-ti': false, 'control-operativo': true };
        case 'Operativo-Inspector':
          return { 'recursos-humanos': false, 'almacen': false, 'sgi': true, 'contabilidad': false, 'transportes': false, 'seguridad': false, 'administracion-ti': false, 'control-operativo': true };
        case 'Almacen':
          return { 'recursos-humanos': false, 'almacen': true, 'sgi': true, 'contabilidad': false, 'transportes': false, 'seguridad': false, 'administracion-ti': false, 'control-operativo': true };
        case 'Recursos Humanos':
          return { 'recursos-humanos': true, 'contabilidad': true };
        case 'Contabilidad':
          return { 'recursos-humanos': true, 'contabilidad': true };
        case 'Transportes':
          return { 'recursos-humanos': false, 'almacen': false, 'sgi': true, 'contabilidad': false, 'transportes': true, 'seguridad': false, 'administracion-ti': false, 'control-operativo': true };
        default:
          return { 'recursos-humanos': true, 'almacen': false, 'sgi': true, 'contabilidad': true, 'transportes': false, 'seguridad': false, 'administracion-ti': false, 'control-operativo': true };
      }
    };
    const roleSectionDefaults = (r) => {
      switch (r) {
        case 'Administrador':
          return {
            'almacen': {
              'marca-item': true,
              'materiales': true,
              'entrada': true,
              'asignacion': true,
              'devoluciones': true,
              'salidas': true,
              'stock': true,
              'consulta-responsable': true,
              'historial': true,
            },
            'recursos-humanos': {
              'personas': true,
            },
            'contabilidad': {
              'libro-diario': true,
              'cuentas': true,
              'facturacion': true,
              'reportes': true,
            },
            'transportes': {
              'flota': true,
              'rutas': true,
              'mantenimiento': true,
              'despachos': true,
            },
            'seguridad': {
              'inspecciones': true,
              'incidentes': true,
              'reportes': true,
            },
            'control-operativo': {
              'planificacion': true,
              'seguimiento': true,
              'incidencias': true,
              'reportes': true,
            },
            'sgi': {
              'formularios': true,
              'indicadores': true,
              'gestion': true,
            },
          };
        case 'Administrativo':
          return {
            'almacen': {
              'marca-item': false,
              'materiales': false,
              'entrada': false,
              'asignacion': false,
              'devoluciones': false,
              'salidas': false,
              'stock': false,
              'consulta-responsable': false,
              'historial': false,
            },
            'recursos-humanos': {
              'personas': false,
            },
            'contabilidad': {
              'libro-diario': false,
              'cuentas': false,
              'facturacion': false,
              'reportes': false,
            },
            'transportes': {
              'flota': false,
              'rutas': false,
              'mantenimiento': false,
              'despachos': false,
            },
            'seguridad': {
              'inspecciones': false,
              'incidentes': false,
              'reportes': false,
            },
            'control-operativo': {
              'planificacion': false,
              'seguimiento': false,
              'incidencias': false,
              'reportes': false,
            },
            'sgi': {
              'formularios': true,
              'indicadores': true,
              'gestion': true,
            },
          };
        case 'Operativo':
          return {
            'almacen': {
              'marca-item': false,
              'materiales': false,
              'entrada': false,
              'asignacion': false,
              'devoluciones': false,
              'salidas': false,
              'stock': false,
              'consulta-responsable': false,
              'historial': false,
            },
            'recursos-humanos': {
              'personas': false,
            },
            'contabilidad': {
              'libro-diario': false,
              'cuentas': false,
              'facturacion': false,
              'reportes': false,
            },
            'transportes': {
              'flota': false,
              'rutas': false,
              'mantenimiento': false,
              'despachos': false,
            },
            'seguridad': {
              'inspecciones': false,
              'incidentes': false,
              'reportes': false,
            },
            'control-operativo': {
              'planificacion': true,
              'seguimiento': true,
              'incidencias': true,
              'reportes': true,
            },
            'sgi': {
              'formularios': true,
              'indicadores': true,
              'gestion': true,
            },
          };
        case 'Operativo-Inspector':
          return {
            'almacen': {
              'marca-item': false,
              'materiales': false,
              'entrada': false,
              'asignacion': false,
              'devoluciones': false,
              'salidas': false,
              'stock': false,
              'consulta-responsable': false,
              'historial': false,
            },
            'recursos-humanos': {
              'personas': false,
            },
            'contabilidad': {
              'libro-diario': false,
              'cuentas': false,
              'facturacion': false,
              'reportes': false,
            },
            'transportes': {
              'flota': false,
              'rutas': false,
              'mantenimiento': false,
              'despachos': false,
            },
            'seguridad': {
              'inspecciones': false,
              'incidentes': false,
              'reportes': false,
            },
            'control-operativo': {
              'planificacion': true,
              'seguimiento': true,
              'incidencias': true,
              'reportes': true,
            },
            'sgi': {
              'formularios': true,
              'indicadores': true,
              'gestion': true,
            },
          };
        case 'Almacen':
          return {
            'almacen': {
              'marca-item': true,
              'materiales': true,
              'entrada': true,
              'asignacion': true,
              'devoluciones': true,
              'salidas': true,
              'stock': true,
              'consulta-responsable': true,
              'historial': true,
            },
            'recursos-humanos': { 'personas': false },
            'contabilidad': { 'libro-diario': false, 'cuentas': false, 'facturacion': false, 'reportes': false },
            'transportes': { 'flota': false, 'rutas': false, 'mantenimiento': false, 'despachos': false },
            'seguridad': { 'inspecciones': false, 'incidentes': false, 'reportes': false },
            'control-operativo': { 'planificacion': false, 'seguimiento': false, 'incidencias': false, 'reportes': false },
            'sgi': { 'formularios': true, 'indicadores': true, 'gestion': true },
          };
        case 'Recursos Humanos':
          return {
            'almacen': { 'marca-item': false, 'materiales': false, 'entrada': false, 'asignacion': false, 'devoluciones': false, 'salidas': false, 'stock': false, 'consulta-responsable': false, 'historial': false },
            'recursos-humanos': { 'personas': true },
            'contabilidad': { 'libro-diario': true, 'cuentas': true, 'facturacion': true, 'reportes': true },
            'transportes': { 'flota': false, 'rutas': false, 'mantenimiento': false, 'despachos': false },
            'seguridad': { 'inspecciones': false, 'incidentes': false, 'reportes': false },
            'control-operativo': { 'planificacion': false, 'seguimiento': false, 'incidencias': false, 'reportes': false },
            'sgi': { 'formularios': false, 'indicadores': false, 'gestion': false },
          };
        case 'Contabilidad':
          return {
            'almacen': { 'marca-item': false, 'materiales': false, 'entrada': false, 'asignacion': false, 'devoluciones': false, 'salidas': false, 'stock': false, 'consulta-responsable': false, 'historial': false },
            'recursos-humanos': { 'personas': true },
            'contabilidad': { 'libro-diario': true, 'cuentas': true, 'facturacion': true, 'reportes': true },
            'transportes': { 'flota': false, 'rutas': false, 'mantenimiento': false, 'despachos': false },
            'seguridad': { 'inspecciones': false, 'incidentes': false, 'reportes': false },
            'control-operativo': { 'planificacion': false, 'seguimiento': false, 'incidencias': false, 'reportes': false },
            'sgi': { 'formularios': false, 'indicadores': false, 'gestion': false },
          };
        case 'Transportes':
          return {
            'almacen': {
              'marca-item': false,
              'materiales': false,
              'entrada': false,
              'asignacion': false,
              'devoluciones': false,
              'salidas': false,
              'stock': false,
              'consulta-responsable': false,
              'historial': false,
            },
            'recursos-humanos': {
              'personas': false,
            },
            'contabilidad': {
              'libro-diario': false,
              'cuentas': false,
              'facturacion': false,
              'reportes': false,
            },
            'transportes': {
              'flota': true,
              'rutas': true,
              'mantenimiento': true,
              'despachos': true,
            },
            'seguridad': { 'inspecciones': false, 'incidentes': false, 'reportes': false },
            'control-operativo': {
              'planificacion': true,
              'seguimiento': true,
              'incidencias': true,
              'reportes': true,
            },
            'sgi': {
              'formularios': true,
              'indicadores': true,
              'gestion': true,
            },
          };
        default:
          return {
            'almacen': {
              'marca-item': false,
              'materiales': false,
              'entrada': false,
              'asignacion': false,
              'devoluciones': false,
              'salidas': false,
              'stock': false,
              'consulta-responsable': false,
              'historial': false,
            },
            'recursos-humanos': {
              'personas': true,
            },
            'contabilidad': {
              'libro-diario': true,
              'cuentas': true,
              'facturacion': true,
              'reportes': true,
            },
            'transportes': {
              'flota': false,
              'rutas': false,
              'mantenimiento': false,
              'despachos': false,
            },
            'seguridad': { 'inspecciones': false, 'incidentes': false, 'reportes': false },
            'control-operativo': {
              'planificacion': true,
              'seguimiento': true,
              'incidencias': true,
              'reportes': true,
            },
            'sgi': {
              'formularios': true,
              'indicadores': true,
              'gestion': true,
            },
          };
      }
    };

    const initialModulePrefs = newUser.modulePrefs || roleModuleDefaults(role);
    const initialSectionPrefs = newUser.sectionPrefs || roleSectionDefaults(role);

    const payload = {
      name: newUser.name,
      email: newUser.email,
      role,
      password: newUser.password,
      warehouse_id: newUser.warehouseId,
      responsible_cedula: newUser.responsibleCedula || null,
      area_name: newUser.areaName || null,
      purchase_can_approve_requests: newUser.purchaseCanApproveRequests ? 1 : 0,
      purchase_can_approve_orders: newUser.purchaseCanApproveOrders ? 1 : 0,
      purchase_can_manage_requests: newUser.purchaseCanManageRequests ? 1 : 0,
      purchase_can_edit_orders: newUser.purchaseCanEditOrders ? 1 : 0,
      purchase_can_manage_payment_status: newUser.purchaseCanManagePaymentStatus ? 1 : 0,
      overtime_final_approver: newUser.overtimeFinalApprover ? 1 : 0,
      preop_auth_sst: newUser.preopAuthSst ? 1 : 0,
      preop_auth_seguridad: newUser.preopAuthSeguridad ? 1 : 0,
      preop_auth_transportes: newUser.preopAuthTransportes ? 1 : 0,
      overtime_can_edit_approved: newUser.overtimeCanEditApproved ? 1 : 0,
      hr_novelties_can_export: newUser.hrNoveltiesCanExport ? 1 : 0,
      expense_can_export: newUser.expenseCanExport ? 1 : 0,
      expense_can_manage: newUser.expenseCanManage ? 1 : 0,
      novelty_hr_can_receive: newUser.noveltyHrCanReceive ? 1 : 0,
      preop_can_approve: newUser.preopCanApprove ? 1 : 0,
      overtime_allowed_projects: newUser.overtimeAllowedProjects || '',
      action: 'create',
    };
    const res = await api.admin.users.upsert(payload);
    const assignedId = res?.id ?? res?.data?.id ?? newUser.id;
    const finalUser = { ...newUser, id: assignedId };
    setState((s) => ({
      ...s,
      users: [...s.users, finalUser],
      modulePrefs: { ...s.modulePrefs, [assignedId]: initialModulePrefs },
      sectionPrefs: { ...s.sectionPrefs, [assignedId]: initialSectionPrefs },
    }));
    try {
      await api.admin.prefs.upsert({
        user_id: assignedId,
        module_prefs: JSON.stringify(initialModulePrefs),
        section_prefs: JSON.stringify(initialSectionPrefs),
      });
    } catch (_) {}
    return finalUser;
  };

  const updateUser = async (id, data) => {
    const existing = state.users.find(u => u.id === id) || {};
    const payload = {
      id,
      name: data.name ?? existing.name ?? '',
      email: data.email ?? existing.email ?? '',
      role: data.role ?? existing.role ?? 'Consulta',
      password: data.password ?? undefined,
      warehouse_id: data.warehouseId ?? existing.warehouseId ?? null,
      responsible_cedula: (data.responsibleCedula ?? existing.responsibleCedula ?? null),
      area_name: (data.areaName ?? existing.areaName ?? null),
      purchase_can_approve_requests: (data.purchaseCanApproveRequests ?? existing.purchaseCanApproveRequests ?? false) ? 1 : 0,
      purchase_can_approve_orders: (data.purchaseCanApproveOrders ?? existing.purchaseCanApproveOrders ?? false) ? 1 : 0,
      purchase_can_manage_requests: (data.purchaseCanManageRequests ?? existing.purchaseCanManageRequests ?? false) ? 1 : 0,
      purchase_can_edit_orders: (data.purchaseCanEditOrders ?? existing.purchaseCanEditOrders ?? false) ? 1 : 0,
      purchase_can_manage_payment_status: (data.purchaseCanManagePaymentStatus ?? existing.purchaseCanManagePaymentStatus ?? false) ? 1 : 0,
      overtime_final_approver: (data.overtimeFinalApprover ?? existing.overtimeFinalApprover ?? false) ? 1 : 0,
      preop_auth_sst: (data.preopAuthSst ?? existing.preopAuthSst ?? false) ? 1 : 0,
      preop_auth_seguridad: (data.preopAuthSeguridad ?? existing.preopAuthSeguridad ?? false) ? 1 : 0,
      preop_auth_transportes: (data.preopAuthTransportes ?? existing.preopAuthTransportes ?? false) ? 1 : 0,
      overtime_can_edit_approved: (data.overtimeCanEditApproved ?? existing.overtimeCanEditApproved ?? false) ? 1 : 0,
      hr_novelties_can_export: (data.hrNoveltiesCanExport ?? existing.hrNoveltiesCanExport ?? false) ? 1 : 0,
      expense_can_export: (data.expenseCanExport ?? existing.expenseCanExport ?? false) ? 1 : 0,
      expense_can_manage: (data.expenseCanManage ?? existing.expenseCanManage ?? false) ? 1 : 0,
      novelty_hr_can_receive: (data.noveltyHrCanReceive ?? existing.noveltyHrCanReceive ?? false) ? 1 : 0,
      preop_can_approve: (data.preopCanApprove ?? existing.preopCanApprove ?? false) ? 1 : 0,
      overtime_allowed_projects: (data.overtimeAllowedProjects ?? existing.overtimeAllowedProjects ?? '') || '',
      action: 'update',
    };
    await api.admin.users.upsert(payload);
    setState((s) => ({
      ...s,
      users: s.users.map(u => u.id === id ? { ...u, ...data } : u),
    }));
  };

  const deleteUser = async (id) => {
    await api.admin.users.delete({ id });
    setState((s) => ({
      ...s,
      users: s.users.filter(u => u.id !== id),
    }));
  };

  const setUserActive = async (id, active) => {
    await api.admin.users.setActive({ id, active: active ? 1 : 0 });
    setState((s) => ({
      ...s,
      users: s.users.map(u => u.id === id ? { ...u, active } : u),
    }));
  };

  const setCurrentUserId = (id) => setState((s) => ({ ...s, currentUserId: id }));

  const getUserByEmail = (email) => (state.users.find(u => String(u.email).toLowerCase() === String(email).toLowerCase()) || null);
  const getUserByCedula = (cedula) => (state.users.find(u => String(u.responsibleCedula || u.cedula || '').trim() === String(cedula).trim()) || null);

  const extractAuthToken = (res) => {
    const t = res?.token ?? res?.access_token ?? res?.jwt ?? res?.auth_token ?? res?.trusted_token ?? res?.data?.token ?? res?.data?.access_token ?? res?.data?.trusted_token ?? null;
    return t ? String(t) : null;
  };
  const persistAuthToken = (raw) => {
    const token = String(raw || '').trim();
    if (!token) return;
    try { sessionStorage.setItem(AUTH_TOKEN_KEY, token); } catch (_) {}
    try { localStorage.setItem(AUTH_TOKEN_KEY, token); } catch (_) {}
  };
  const clearAuthToken = () => {
    try { sessionStorage.removeItem(AUTH_TOKEN_KEY); } catch (_) {}
    try { localStorage.removeItem(AUTH_TOKEN_KEY); } catch (_) {}
  };
  const hasAuthToken = () => {
    try {
      const s = sessionStorage.getItem(AUTH_TOKEN_KEY);
      if (s) return true;
    } catch (_) {}
    try {
      const l = localStorage.getItem(AUTH_TOKEN_KEY);
      if (l) return true;
    } catch (_) {}
    return false;
  };

  const authenticate = async (identifier, password) => {
    const input = String(identifier || '').trim();
    const lowered = input.toLowerCase();
    
    // Fallback local para brigada (mantener lógica existente)
    if (lowered === 'brigada@senerfi.com') {
      const isEmail = lowered.includes('@');
      let user = isEmail ? getUserByEmail(lowered) : getUserByCedula(input);
      if (!user) {
        const seedUser = { id: 'briguser1', name: 'Brigada', email: 'brigada@senerfi.com', role: 'Administrativo', password: 'test123', warehouseId: 'popayan', responsibleCedula: '9001' };
        const role = seedUser.role || 'Administrativo';
        const initialModulePrefs = seedUser.modulePrefs || ((r) => {
          switch (r) {
            case 'Administrador': return { 'recursos-humanos': true, 'almacen': true, 'sgi': true, 'contabilidad': true, 'transportes': true, 'seguridad': true, 'administracion-ti': true, 'control-operativo': true };
            case 'Administrativo': return { 'sgi': true };
            default: return { 'sgi': true };
          }
        })(role);
        const initialSectionPrefs = seedUser.sectionPrefs || ((r) => { return { 'sgi': { 'formularios': true, 'indicadores': true, 'gestion': true } }; })(role);
        
        setState((s) => ({
          ...s,
          users: [...s.users, seedUser],
          modulePrefs: { ...s.modulePrefs, briguser1: initialModulePrefs },
          sectionPrefs: { ...s.sectionPrefs, briguser1: initialSectionPrefs },
        }));
        user = seedUser;
      }
      if (String(user.password) !== String(password)) return { success: false, message: 'Contraseña incorrecta' };
      setCurrentUserId(user.id);
      return { success: true, user, message: 'Inicio de sesión exitoso' };
    }

    if (canUseSigMobileOfflineAuth() && typeof navigator !== 'undefined' && navigator.onLine === false) {
      const offlineUser = await resolveSigMobileOfflineSession(identifier, password);
      if (offlineUser) {
        setState((s) => {
          const exists = s.users.some((u) => String(u.id) === String(offlineUser.id));
          const nextUsers = exists
            ? s.users.map((u) => (String(u.id) === String(offlineUser.id) ? { ...u, ...offlineUser } : u))
            : [...s.users, offlineUser];
          return { ...s, users: nextUsers, currentUserId: offlineUser.id };
        });
        return { success: true, user: offlineUser, offline: true };
      }
      return { success: false, message: 'Sin internet y no hay una sesión previa disponible en este dispositivo.' };
    }

    // Autenticación real vía API
    try {
      const res = await api.auth.login({ email: identifier, password });
      if (!res || typeof res !== 'object') {
        return { success: false, message: 'Respuesta inválida del servidor' };
      }
      if (res.require_totp_setup) {
        return {
          success: true,
          require_totp_setup: true,
          userId: res.user_id,
          enrollmentToken: res.enrollment_token,
          message: res.message,
        };
      }
      if (res.require_2fa) {
        return {
          success: true,
          require_2fa: true,
          userId: res.user_id,
          message: res.message,
          method: res.method || 'email',
          recipient: res.recipient || res.recipient_masked || null,
        };
      }
      if (res.pending_totp_approval && res.blocked) {
        // Ya activó el 2FA por app, pero un Administrador todavía no lo
        // aprueba: NO se otorga acceso (ver server/api/login.php).
        return { success: false, message: res.message, pendingApprovalBlocked: true };
      }
      if (res.ok && res.user) {
        // Login exitoso directo (si no requiere 2FA)
        const user = normalizePermissionFlags(res.user);
        persistAuthToken(extractAuthToken(res));
        await persistSigMobileOfflineSession({ user, identifier, password });
        setState(s => {
          const exists = s.users.some(u => u.id === user.id);
          const newUsers = exists ? s.users.map(u => u.id === user.id ? { ...u, ...user } : u) : [...s.users, user];
          return { ...s, users: newUsers, currentUserId: user.id };
        });
        return { success: true, user };
      }
      return { success: false, message: 'Credenciales inválidas' };
    } catch (e) {
      if (canUseSigMobileOfflineAuth()) {
        const offlineUser = await resolveSigMobileOfflineSession(identifier, password);
        if (offlineUser) {
          setState((s) => {
            const exists = s.users.some((u) => String(u.id) === String(offlineUser.id));
            const nextUsers = exists
              ? s.users.map((u) => (String(u.id) === String(offlineUser.id) ? { ...u, ...offlineUser } : u))
              : [...s.users, offlineUser];
            return { ...s, users: nextUsers, currentUserId: offlineUser.id };
          });
          return { success: true, user: offlineUser, offline: true };
        }
      }
      return { success: false, message: e.message || 'Error al conectar con el servidor' };
    }
  };

  const verify2FA = async (userId, code) => {
    try {
      const res = await api.auth.verify2fa({ user_id: userId, code });
      if (res.ok && res.user) {
        const user = normalizePermissionFlags(res.user);
        persistAuthToken(extractAuthToken(res));
        setState(s => {
          const exists = s.users.some(u => u.id === user.id);
          const newUsers = exists ? s.users.map(u => u.id === user.id ? { ...u, ...user } : u) : [...s.users, user];
          return { ...s, users: newUsers, currentUserId: user.id };
        });
        return { success: true, user };
      }
      return { success: false, message: 'Código inválido' };
    } catch (e) {
      return { success: false, message: e.message || 'Error al verificar código' };
    }
  };

  const startTotpEnrollment = async (userId, enrollmentToken) => {
    try {
      const res = await api.auth.totpEnroll.start({ user_id: userId, enrollment_token: enrollmentToken });
      return res;
    } catch (e) {
      return { ok: false, error: e.message || 'Error al generar la clave' };
    }
  };

  const completeTotpEnrollment = async (userId, enrollmentToken, code) => {
    try {
      const res = await api.auth.totpEnroll.confirm({ user_id: userId, enrollment_token: enrollmentToken, code });
      if (res.ok && res.pending_totp_approval && res.blocked) {
        // Código confirmado, pero todavía NO hay acceso: falta que un
        // Administrador lo apruebe (ver server/api/totp_enroll.php).
        return { success: true, pendingApprovalBlocked: true, message: res.message };
      }
      if (res.ok && res.user) {
        const user = normalizePermissionFlags(res.user);
        persistAuthToken(extractAuthToken(res));
        setState(s => {
          const exists = s.users.some(u => u.id === user.id);
          const newUsers = exists ? s.users.map(u => u.id === user.id ? { ...u, ...user } : u) : [...s.users, user];
          return { ...s, users: newUsers, currentUserId: user.id };
        });
        return { success: true, user };
      }
      return { success: false, message: res.error || 'Código inválido' };
    } catch (e) {
      return { success: false, message: e.message || 'Error al verificar código' };
    }
  };

  const resend2FA = async (userId) => {
    try {
      const res = await api.auth.resend2fa({ user_id: userId });
      if (res.ok) {
        return { success: true, message: res.message, recipient: res.recipient || res.recipient_masked || null };
      }
      return { success: false, message: res.error || 'Error al reenviar código' };
    } catch (e) {
      return { success: false, message: e.message || 'Error de conexión' };
    }
  };

  const logout = () => {
    clearAuthToken();
    clearSigMobileOfflineSession();
    setCurrentUserId(null);
    return { success: true };
  };

  const getModulePrefs = (userId) => {
    const uid = userId ?? state.currentUserId;
    const base = state.modulePrefs?.[uid] || {};
    const allowed = state.modulePrefs?.[-1] || {};
    const user = users.find(u => u.id === uid);
    const isAdmin = (user?.role === 'Administrador');
    if (isAdmin) {
      return { ...allowed, ...base };
    }
    const allowedKeys = Object.keys(allowed || {}).filter((k) => allowed && allowed[k]);
    if (allowedKeys.length === 0) {
      const baseHasAny = Object.keys(base || {}).some((k) => !!(base && base[k]));
      if (!baseHasAny) {
        const role = user?.role || 'Administrativo';
        const roleModuleDefaults = (r) => {
          switch (r) {
            case 'Administrador':
              return { 'recursos-humanos': true, 'almacen': true, 'sgi': true, 'contabilidad': true, 'transportes': true, 'seguridad': true, 'administracion-ti': true, 'control-operativo': true };
            case 'Administrativo':
              return { 'sgi': true };
            case 'Operativo':
              return { 'recursos-humanos': false, 'almacen': false, 'sgi': true, 'contabilidad': false, 'transportes': false, 'seguridad': false, 'administracion-ti': false, 'control-operativo': true };
            case 'Operativo-Inspector':
              return { 'recursos-humanos': false, 'almacen': false, 'sgi': true, 'contabilidad': false, 'transportes': false, 'seguridad': false, 'administracion-ti': false, 'control-operativo': true };
            case 'Almacen':
              return { 'recursos-humanos': false, 'almacen': true, 'sgi': true, 'contabilidad': false, 'transportes': false, 'seguridad': false, 'administracion-ti': false, 'control-operativo': true };
            case 'Recursos Humanos':
              return { 'recursos-humanos': true, 'contabilidad': true };
            case 'Contabilidad':
              return { 'recursos-humanos': true, 'contabilidad': true };
            case 'Transportes':
              return { 'recursos-humanos': false, 'almacen': false, 'sgi': true, 'contabilidad': false, 'transportes': true, 'seguridad': false, 'administracion-ti': false, 'control-operativo': true };
            default:
              return { 'recursos-humanos': true, 'almacen': false, 'sgi': true, 'contabilidad': true, 'transportes': false, 'seguridad': false, 'administracion-ti': false, 'control-operativo': true };
          }
        };
        const defaults = roleModuleDefaults(role) || {};
        const out = {};
        Object.keys(defaults || {}).forEach((k) => { if (defaults[k]) out[k] = true; });
        return out;
      }
      const out = {};
      Object.keys(base).forEach((k) => { if (base[k]) out[k] = true; });
      return out;
    }
    const out = {};
    allowedKeys.forEach((k) => { out[k] = !!base[k]; });
    return out;
  };

  const getModulePrefsRaw = (userId) => {
    const uid = userId ?? state.currentUserId;
    return { ...(state.modulePrefs?.[uid] || {}) };
  };

  const setModulePrefs = (userId, prefs) => {
    const uid = userId ?? state.currentUserId;
    setState((s) => ({
      ...s,
      modulePrefs: { ...s.modulePrefs, [uid]: { ...prefs } },
    }));
    (async () => {
      try {
        const section = state.sectionPrefs?.[uid] || {};
        await api.admin.prefs.upsert({ user_id: uid, module_prefs: JSON.stringify(prefs), section_prefs: JSON.stringify(section) });
      } catch (_) {}
    })();
  };

  const setSectionPrefs = (userId, moduleKey, prefs) => {
    const uid = userId ?? state.currentUserId;
    const nextSection = { ...(state.sectionPrefs?.[uid] || {}), [moduleKey]: { ...prefs } };
    setState((s) => ({
      ...s,
      sectionPrefs: {
        ...s.sectionPrefs,
        [uid]: nextSection,
      },
    }));
    (async () => {
      try {
        const modulePrefs = state.modulePrefs?.[uid] || {};
        await api.admin.prefs.upsert({ user_id: uid, module_prefs: JSON.stringify(modulePrefs), section_prefs: JSON.stringify(nextSection) });
      } catch (_) {}
    })();
  };

  const getSectionPrefs = (userId) => {
    const uid = userId ?? state.currentUserId;
    const base = state.sectionPrefs?.[uid] || {};
    const allowed = state.sectionPrefs?.[-1] || {};
    const user = users.find(u => u.id === uid);
    const isAdmin = (user?.role === 'Administrador');
    if (isAdmin) {
      return { ...allowed, ...base };
    }
    const out = {};
    Object.keys(allowed).forEach((modKey) => {
      const allowedSec = allowed[modKey] || {};
      const userSec = base[modKey] || {};
      const secOut = {};
      Object.keys(allowedSec).forEach((sk) => { if (allowedSec[sk]) secOut[sk] = !!userSec[sk]; });
      if (Object.keys(secOut).length) out[modKey] = secOut;
    });
    return out;
  };

  const getSectionPrefsRaw = (userId) => {
    const uid = userId ?? state.currentUserId;
    return { ...(state.sectionPrefs?.[uid] || {}) };
  };

  // Helper: detectar si hay preferencias almacenadas para un usuario
  const hasStoredPrefs = (userId) => {
    const uid = userId ?? state.currentUserId;
    const mpHas = !!(state.modulePrefs && Object.prototype.hasOwnProperty.call(state.modulePrefs, uid));
    const spHas = !!(state.sectionPrefs && Object.prototype.hasOwnProperty.call(state.sectionPrefs, uid));
    return mpHas || spHas;
  };

  // Defaults: obtener y guardar permisos por defecto (usuario virtual -1)
  const getDefaultPrefs = () => ({ module: getModulePrefs(-1), section: getSectionPrefs(-1) });
  const saveDefaultPrefs = async (modulePrefs, sectionPrefs) => {
    // Actualizar estado local
    setState((s) => ({
      ...s,
      modulePrefs: { ...s.modulePrefs, [-1]: { ...modulePrefs } },
      sectionPrefs: { ...s.sectionPrefs, [-1]: { ...sectionPrefs } },
    }));
    // Persistir al backend (usuario virtual -1)
    try {
      await api.admin.prefs.upsert({ user_id: -1, module_prefs: JSON.stringify(modulePrefs), section_prefs: JSON.stringify(sectionPrefs) });
    } catch (_) {}
  };

  // Asegurar que el usuario admin@senerfi.com tenga todos los módulos/secciones activados
  useEffect(() => {
    try {
      const admin = users.find(u => String(u.email).toLowerCase() === 'admin@senerfi.com');
      if (!admin) return;
      const adminId = admin.id;
      const existingModules = state.modulePrefs?.[adminId] || {};
      const existingSections = state.sectionPrefs?.[adminId] || {};
      const allModules = {
        'recursos-humanos': true,
        'almacen': true,
        'sgi': true,
        'contabilidad': true,
        'transportes': true,
        'seguridad': true,
        'administracion-ti': true,
        'control-operativo': true,
      };
      const allSections = {
        'almacen': {
          'marca-item': true,
          'materiales': true,
          'bodegas': true,
          'pedidos': true,
          'entrada': true,
          'asignacion': true,
          'prestamo': true,
          'devoluciones': true,
          'salidas': true,
          'traslados': true,
          'consumibles': true,
          'stock': true,
          'consulta-responsable': true,
          'consulta-serial': true,
          'historial': true,
          'alertas': true,
          'indicadores': true,
        },
        'recursos-humanos': { 'personas': true },
        'contabilidad': { 'libro-diario': true, 'cuentas': true, 'facturacion': true, 'reportes': true, 'solicitudes': true, 'proveedores': true, 'centros-costo': true, 'novedades': true, 'solicitud-gastos': true },
        'transportes': { 'flota': true, 'rutas': true, 'mantenimiento': true, 'despachos': true },
        'seguridad': { 'inspecciones': true, 'incidentes': true, 'reportes': true },
        'control-operativo': { 'planificacion': true, 'seguimiento': true, 'incidencias': true, 'reportes': true },
      };
      const needsModules = Object.keys(allModules).some((k) => existingModules[k] !== true);
      const needsSections = Object.keys(allSections).some((mk) => {
        const sec = existingSections[mk] || {};
        return Object.keys(allSections[mk]).some((sk) => sec[sk] !== true);
      });
      if (needsModules || needsSections) {
        const nextModules = { ...existingModules, ...allModules };
        const nextSections = {
          ...existingSections,
          ...Object.fromEntries(Object.entries(allSections).map(([mk, sec]) => [mk, { ...(existingSections[mk] || {}), ...sec }]))
        };
        setState((s) => ({
          ...s,
          modulePrefs: { ...s.modulePrefs, [adminId]: nextModules },
          sectionPrefs: { ...s.sectionPrefs, [adminId]: nextSections },
        }));
        (async () => {
          try {
            let backendId = adminId;
            try {
              const r = await api.admin.users.list({ q: 'admin@senerfi.com', limit: 1 });
              const found = Array.isArray(r?.data) ? r.data[0] : null;
              if (found?.id) backendId = Number(found.id);
              if (!found) {
                const r2 = await api.admin.users.upsert({
                  name: admin.name || 'Admin TI',
                  email: admin.email || 'admin@senerfi.com',
                  role: admin.role || 'Administrador',
                  password: admin.password || '1234',
                  warehouse_id: admin.warehouseId || 'popayan',
                  responsible_cedula: admin.responsibleCedula || null,
                  action: 'create',
                });
                backendId = Number(r2?.id ?? r2?.data?.id ?? backendId);
              }
              if (backendId !== adminId) {
                setState((s) => {
                  const nextUsers = (Array.isArray(s.users) ? s.users : []).map((u) => u.id === adminId ? { ...u, id: backendId } : u);
                  const mp = { ...(s.modulePrefs || {}) };
                  const sp = { ...(s.sectionPrefs || {}) };
                  mp[backendId] = mp[backendId] || nextModules;
                  sp[backendId] = sp[backendId] || nextSections;
                  delete mp[adminId];
                  delete sp[adminId];
                  return { ...s, users: nextUsers, modulePrefs: mp, sectionPrefs: sp, currentUserId: s.currentUserId === adminId ? backendId : s.currentUserId };
                });
              }
              await api.admin.prefs.upsert({ user_id: backendId, module_prefs: JSON.stringify(nextModules), section_prefs: JSON.stringify(nextSections) });
            } catch (_) {}
          } catch (_) {}
        })();
      }
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const value = {
    users,
    currentUser,
    addUser,
    updateUser,
    deleteUser,
    setUserActive,
    setCurrentUserId,
    authenticate,
    verify2FA,
    resend2FA,
    startTotpEnrollment,
    completeTotpEnrollment,
    logout,
    getModulePrefs,
    getModulePrefsRaw,
    setModulePrefs,
    getSectionPrefs,
    getSectionPrefsRaw,
    setSectionPrefs,
    getDefaultPrefs,
    saveDefaultPrefs,
    hasStoredPrefs,
  };

  return (
    <UserPrefsContext.Provider value={value}>
      {children}
    </UserPrefsContext.Provider>
  );
}

export function useUserPrefs() {
  const ctx = useContext(UserPrefsContext);
  if (!ctx) throw new Error('useUserPrefs must be used within UserPrefsProvider');
  return ctx;
}
